import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, XCircle, UserPlus, ArrowLeft } from '@phosphor-icons/react'
import { DotLoader } from '@/components/ui/DotLoader'
import { useAuthStore } from '@/stores/authStore'

/**
 * WorkOS SSO landing page. The backend `/api/auth/sso/callback` exchanges the
 * authorization code, funnels through the shared login chokepoint, and redirects
 * here carrying the freshly-minted app JWTs (or an `error` code). We hand the
 * tokens to the auth store (same session model as password login) and continue
 * to the originally-requested destination. Modeled on LinkedInCallbackPage.
 */

/** How long the handoff may spin before we offer a manual way out. */
const STALL_AFTER_MS = 12000
/** Grace period so the user can read the failure before we bounce them. */
const ERROR_REDIRECT_MS = 2500

/**
 * One entry per `error` code the backend can redirect back with (see
 * routes/auth.py `sso_callback`), plus a fallback for anything unmapped.
 * `invite_required` isn't really a failure — it's "you don't have an
 * account yet" — so it gets its own tone and an actual way forward instead
 * of being lumped in with real auth failures and bounced to a sign-in page
 * that can't help either.
 */
type ErrorContent = {
  tone: 'error' | 'info'
  title: string
  description: string
  /** Skip the auto-redirect-to-sign-in — used when sign-in isn't the fix. */
  noAutoRedirect?: boolean
}

const ERROR_CONTENT: Record<string, ErrorContent> = {
  invite_required: {
    tone: 'info',
    title: 'No account found for this email',
    description:
      "This email isn't on an existing account yet. Ask whoever manages your workspace to send you an invite, or create your own account to get started.",
    noAutoRedirect: true,
  },
  auth_failed: {
    tone: 'error',
    title: 'Sign-in failed',
    description: 'Something went wrong completing sign-in with your identity provider. Please try again.',
  },
  sso_disabled: {
    tone: 'error',
    title: 'Single sign-on is unavailable',
    description: 'Single sign-on isn’t turned on for this workspace right now. Sign in with your email and password instead.',
  },
  missing_code: {
    tone: 'error',
    title: 'Sign-in link incomplete',
    description: 'That sign-in link is missing required information. Start over from the sign-in page.',
  },
}

const DEFAULT_ERROR_CONTENT: ErrorContent = {
  tone: 'error',
  title: 'Sign-in was cancelled',
  description: 'The sign-in attempt didn’t complete. If you didn’t start this, you can safely ignore it.',
}

const CallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loginWithTokens } = useAuthStore()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const [errorContent, setErrorContent] = useState<ErrorContent>(DEFAULT_ERROR_CONTENT)
  // A handoff that never settles (stalled network) previously left a spinner
  // with no exit. After STALL_AFTER_MS we surface an escape hatch.
  const [stalled, setStalled] = useState(false)
  // StrictMode mounts effects twice in dev; guard the one-shot token handoff.
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current) return
    handled.current = true

    const error = searchParams.get('error')
    const accessToken = searchParams.get('access_token')
    const refreshToken = searchParams.get('refresh_token')
    const redirectTo = searchParams.get('redirect') || '/dashboard'

    const timers: number[] = []
    const failWith = (content: ErrorContent) => {
      setStatus('error')
      setErrorContent(content)
      if (!content.noAutoRedirect) {
        timers.push(window.setTimeout(() => navigate('/login', { replace: true }), ERROR_REDIRECT_MS))
      }
    }

    if (error || !accessToken || !refreshToken) {
      failWith(error ? ERROR_CONTENT[error] ?? DEFAULT_ERROR_CONTENT : DEFAULT_ERROR_CONTENT)
      return () => timers.forEach(window.clearTimeout)
    }

    timers.push(window.setTimeout(() => setStalled(true), STALL_AFTER_MS))

    loginWithTokens(accessToken, refreshToken)
      .then(() => {
        setStatus('success')
        setMessage('Signed in successfully')
        // Clear tokens from the URL/history before continuing.
        navigate(redirectTo, { replace: true })
      })
      .catch(() =>
        failWith({
          tone: 'error',
          title: 'Sign-in failed',
          description: 'We couldn’t finish signing you in. Please try again.',
        }),
      )

    // Timers must not outlive the component — a pending navigate() firing after
    // unmount yanks the user off whatever page they reached.
    return () => timers.forEach(window.clearTimeout)
  }, [searchParams, navigate, loginWithTokens])

  const bodyStyle = { fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' } as const
  const captionStyle = { fontSize: '12px', letterSpacing: '-0.1px' } as const

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-white dark:bg-[#0f1011] px-6">
      <div className="text-center space-y-4">
        {status === 'loading' && (
          // One live region for the whole pending block so the stall message is
          // announced too; the loader inside is decorative (§55 — no double-speak).
          <div role="status" aria-live="polite" className="space-y-4">
            <div className="flex justify-center text-[#5e6ad2] dark:text-[#7170ff]">
              <DotLoader size="lg" announce={false} />
            </div>
            <p className="text-[#5f6166] dark:text-[#8a8f98]" style={bodyStyle}>
              Completing sign-in…
            </p>
            {stalled && (
              <div className="space-y-2 pt-1">
                <p className="text-[#5f6166] dark:text-[#8a8f98]" style={captionStyle}>
                  This is taking longer than usual.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/login', { replace: true })}
                  className="inline-flex items-center justify-center gap-1.5 text-[#5f6166] hover:text-[#0f1011] dark:text-[#8a8f98] dark:hover:text-[#d0d6e0] transition-colors"
                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
                  Back to sign in
                </button>
              </div>
            )}
          </div>
        )}
        {status === 'success' && (
          <div role="status" aria-live="polite" className="space-y-4">
            <CheckCircle
              className="h-8 w-8 text-emerald-600 dark:text-emerald-400 mx-auto"
              weight="fill"
            />
            <p className="text-emerald-700 dark:text-emerald-400" style={bodyStyle}>
              {message}
            </p>
            <p className="text-[#5f6166] dark:text-[#8a8f98]" style={captionStyle}>
              Redirecting…
            </p>
          </div>
        )}
        {status === 'error' && (
          <div role="alert" className="space-y-4 max-w-[340px]">
            {errorContent.tone === 'info' ? (
              <UserPlus className="h-8 w-8 text-[#5e6ad2] dark:text-[#7170ff] mx-auto" weight="fill" />
            ) : (
              <XCircle className="h-8 w-8 text-red-500 dark:text-red-400 mx-auto" weight="fill" />
            )}
            <div className="space-y-1.5">
              <p
                className={
                  errorContent.tone === 'info'
                    ? 'text-[#0f1011] dark:text-[#f7f8f8]'
                    : 'text-red-600 dark:text-red-400'
                }
                style={bodyStyle}
              >
                {errorContent.title}
              </p>
              <p className="text-[#5f6166] dark:text-[#8a8f98]" style={captionStyle}>
                {errorContent.description}
              </p>
            </div>
            {!errorContent.noAutoRedirect && (
              <p className="text-[#5f6166] dark:text-[#8a8f98]" style={captionStyle}>
                Redirecting to sign in…
              </p>
            )}
            <div className="flex items-center justify-center gap-4">
              {errorContent.noAutoRedirect && (
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-md bg-[#5e6ad2] hover:bg-[#4e59c0] dark:bg-[#7170ff] dark:hover:bg-[#8482ff] text-white px-3 py-1.5 transition-colors"
                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                >
                  Create account
                </Link>
              )}
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="inline-flex items-center justify-center gap-1.5 text-[#5f6166] hover:text-[#0f1011] dark:text-[#8a8f98] dark:hover:text-[#d0d6e0] transition-colors"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
                {errorContent.noAutoRedirect ? 'Back to sign in' : 'Go to sign in now'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CallbackPage
